/**
 * SectionCard — canonical collapsible/expandable section card.
 *
 * Replaces divergent SectionCard implementations across showcases.
 * Used by: ReportShowcase, ComparisonShowcase, TutorialShowcase, AnalysisShowcase,
 *          and as a reference for StudyShowcase / BibleStudyShowcase migrations.
 *
 * Variants: 'default', 'highlighted', 'subtle', 'success', 'warning', 'danger', 'quote'
 *
 * Props:
 *   title        — section heading
 *   children     — section content
 *   borderColor  — override left-border accent color
 *   variant      — visual variant
 *   collapsible  — show expand/collapse toggle (default false)
 *   defaultOpen  — if collapsible, start open (default true)
 *   icon         — optional icon before title
 *   style        — container style override
 */
import React, { useState } from 'react';
import { base } from '../tokens/colors';

const VARIANT_MAP = {
  default:     { bg: base.card,     bdr: base.border,      leftBdr: base.border      },
  highlighted: { bg: '#0d1a2e',     bdr: 'rgba(59,130,246,0.18)', leftBdr: '#3B82F6' },
  subtle:      { bg: 'transparent', bdr: base.borderSoft,  leftBdr: base.borderSoft  },
  success:     { bg: 'rgba(34,197,94,0.05)',  bdr: 'rgba(34,197,94,0.18)',  leftBdr: '#22c55e' },
  warning:     { bg: 'rgba(245,158,11,0.05)', bdr: 'rgba(245,158,11,0.18)', leftBdr: '#f59e0b' },
  danger:      { bg: 'rgba(239,68,68,0.05)',  bdr: 'rgba(239,68,68,0.18)',  leftBdr: '#ef4444' },
  quote:       { bg: 'rgba(167,139,250,0.06)',bdr: 'rgba(167,139,250,0.2)', leftBdr: '#a78bfa' },
};

export function SectionCard({
  title,
  children,
  borderColor,
  variant = 'default',
  collapsible = false,
  defaultOpen = true,
  icon,
  style,
}) {
  const [open, setOpen] = useState(defaultOpen);
  const vs = VARIANT_MAP[variant] || VARIANT_MAP.default;
  const leftBdr = borderColor || vs.leftBdr;

  return (
    <div style={{
      background:  vs.bg,
      border:      `1px solid ${vs.bdr}`,
      borderLeft:  `3px solid ${leftBdr}`,
      borderRadius: 10,
      overflow:    'hidden',
      marginBottom: 0,
      ...style,
    }}>
      {/* Header row */}
      <button
        onClick={collapsible ? () => setOpen(o => !o) : undefined}
        aria-expanded={collapsible ? open : undefined}
        style={{
          display:       'flex',
          alignItems:    'center',
          justifyContent:'space-between',
          width:         '100%',
          padding:       '12px 14px',
          background:    'transparent',
          border:        'none',
          cursor:        collapsible ? 'pointer' : 'default',
          outline:       'none',
          fontFamily:    'inherit',
          textAlign:     'left',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
          {icon && <span style={{ fontSize: 14, flexShrink: 0 }}>{icon}</span>}
          <span style={{
            fontSize:       11,
            fontWeight:     800,
            letterSpacing:  '0.08em',
            textTransform:  'uppercase',
            color:          base.textMuted,
            lineHeight:     1.2,
          }}>
            {title}
          </span>
        </div>
        {collapsible && (
          <span style={{
            fontSize:   12,
            color:      base.textMuted,
            transform:  open ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.18s ease',
            flexShrink: 0,
            marginLeft: 8,
          }}>▼</span>
        )}
      </button>

      {/* Content */}
      {(!collapsible || open) && (
        <div style={{ padding: '0 14px 12px' }}>
          {children}
        </div>
      )}
    </div>
  );
}

export default SectionCard;
